<script  lang="ts">
  import AddCashier from "@frontend/Components/AddCashier.svelte";
  import EditCashierForm from "@frontend/Components/EditCashierForm.svelte";
  import Modal from "@frontend/Components/Modal.svelte";
  import { service } from "@frontend/services";
  import { usersByRole } from "@frontend/userlist";
  import { groups, RefreshGroups } from "@frontend/grouplist";
  import { type AuthenticatedRequestOfDisableUserDTO, type User, UserRole } from "@frontend/walletapi";
  import { push } from "svelte-spa-router";
  import { get } from "svelte/store";
  import { GetCachedToken, me } from "@frontend/user";
  import { onMount } from "svelte";
  import { translate, currentLanguage } from "../Translator";
  import { formatMoney } from "./Formats";
 
  type EditAction = {
    action: "edit",
    userid: number;
  };

  type AddAction = {
    action: "add"
  };

  type RouteParams =
    EditAction |
    AddAction |
    undefined;
  
  export let params: RouteParams = undefined;

  let selectedUser: User | undefined = undefined;
  $: if (params) {
    if (params.action == "edit") {
      let selectedUserId = params.userid;
      selectedUser = get(usersByRole[UserRole.Cashier]).find(p => p.Id == selectedUserId);
    }
  }

  let allCashiers: User[] = [];
  let searchCashiers: User[] = [];
  let searchInput: string = "";

  let message: { text: string; type: string } = { text: '', type: '' };

  // Función para verificar si un usuario pertenece a mi jerarquía de grupos
  function isUserInMyGroupHierarchy(user: User): boolean {
    // Si soy Owner, veo todos los cashiers
    if ($me?.Role === UserRole.Owner) {
      return true;
    }

    // Si soy Admin, veo cashiers de mi grupo y grupos hijos
    if ($me?.Role === UserRole.Admin) {
      // Si el usuario no tiene grupo, no lo muestro
      if (!user.GroupId || !$me?.GroupId) {
        return false;
      }

      // Si está en mi grupo
      if (user.GroupId === $me.GroupId) {
        return true;
      }

      // Si está en un grupo hijo directo
      if (user.Group?.ParentId === $me.GroupId) {
        return true;
      }

      // Si está en un grupo nieto o más profundo
      let currentGroup = user.Group;
      while (currentGroup?.ParentId) {
        // Si encontramos que algún ancestro es nuestro grupo, incluimos al cashier
        if (currentGroup.ParentId === $me.GroupId) {
          return true;
        }

        // Buscar el siguiente grupo padre en el store de grupos
        currentGroup = get(groups).find(g => g.Id === currentGroup?.ParentId);
      }

      return false;
    }

    // Si soy Cashier, solo veo cashiers de mi mismo grupo
    if ($me?.Role === UserRole.Cashier) {
      // Si el usuario no tiene grupo, no lo muestro
      if (!user.GroupId || !$me?.GroupId) {
        return false;
      }

      // Solo veo cashiers de mi mismo grupo
      if (user.GroupId === $me.GroupId) {
        return true;
      }

      return false;
    }

    // Por defecto, no mostrar nada
    return false;
  }

  // Filtrar cashiers por grupo y búsqueda
  usersByRole[UserRole.Cashier].subscribe((cashiers) => {
    // Primero filtrar por grupo
    allCashiers = cashiers.filter(isUserInMyGroupHierarchy);
    filterCashiers();
  });

  function filterCashiers() {
    if (searchInput == '') {
      searchCashiers = allCashiers;
    } else {
      let searchInputNormalized = searchInput.toLowerCase();
      searchCashiers = allCashiers.filter((cashier) =>
        cashier.Email?.toLowerCase().includes(searchInputNormalized) ||
        cashier.FirstName?.toLowerCase().includes(searchInputNormalized) ||
        cashier.LastName?.toLowerCase().includes(searchInputNormalized)
      );
    }
  }
  
  function dismiss() {
    push("/CashierList");
  }

  $: {
    searchInput;
    searchChanged();
  }

  function clearSearch() {
    searchInput = '';
  }

  let searchTimer: number = 0;
  function searchChanged() {
    if (searchTimer != 0) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(applySearch, 500)
  }

  function applySearch() {
    searchTimer = 0;
    filterCashiers();
  }

  function IsSearchAndNoPlayersMatch(searchCashiers: User[]) {
    return searchInput && searchCashiers.length == 0;
  }

  function IsSearch(searchInput: string) {
    return searchInput && searchInput.length > 0;
  }

  async function DisablePlayer(user: User) {
    try {
      // Actualizar el estado inmediatamente en la interfaz
      user.Enabled = !user.Enabled;
      
      const result = await service.walletapi.disableUser({
        Token: GetCachedToken(),
        Data: {
          Enabled: user.Enabled,
          Id: user.Id
        }
      });

      if (result.Success) {
        // Actualizar el usuario en el store de Cashiers
        const cashiers = get(usersByRole[UserRole.Cashier]);
        const updatedCashiers = cashiers.map(c => 
          c.Id === user.Id ? { ...c, Enabled: user.Enabled } : c
        );
        usersByRole[UserRole.Cashier].set(updatedCashiers);

        message = {
          text: `Cashier ${user.FirstName} has been ${user.Enabled ? 'enabled' : 'disabled'} successfully`,
          type: user.Enabled ? 'success' : 'negative'
        };
      } else {
        // Si hay error, revertir el cambio
        user.Enabled = !user.Enabled;
        message = {
          text: result.Message || 'Error updating cashier status',
          type: 'error'
        };
      }
    } catch (err) {
      // Si hay error, revertir el cambio
      user.Enabled = !user.Enabled;
      message = {
        text: err instanceof Error ? err.message : 'An unexpected error occurred',
        type: 'error'
      };
    }

    // Auto-hide message after 3 seconds
    setTimeout(() => {
      message = { text: '', type: '' };
    }, 3000);
  }

  onMount(() => {
    // Verificar autenticación primero
    if (!$me) {
      push("/user/Login");
      return;
    }
    
    // Cargar grupos para el filtrado
    RefreshGroups();
  });
</script>

<h1 class="ui centered dividing header">{$translate('List of Cashiers')} ({allCashiers?.length ?? ""})</h1>
<div class="ui action input">
  <input type="text" bind:value={searchInput} placeholder="Search..." autocomplete="off">
  <button class="ui icon button" on:click={clearSearch}>
    <i class="times icon"></i>
  </button>
</div>
{#if $me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin}
  <a href="#/CashierList/add" class="ui green button">
    <i class="user plus icon"></i>
    {$translate('Add user')}
  </a>
{/if}

{#if params?.action == "add"}
  <Modal dismiss={dismiss} size="large">
    <AddCashier dismiss={dismiss}></AddCashier>
  </Modal>
{/if}

{#if params?.action == "edit"}
  <Modal dismiss={dismiss} size="large">
    {#if !selectedUser}
      <div class="ui negative message">
        <div class="header">{$translate('No such User')}</div>
      </div>
    {:else}
      <EditCashierForm player={selectedUser} dismiss={dismiss}></EditCashierForm>
    {/if}
  </Modal>
{/if}

{#if IsSearchAndNoPlayersMatch(searchCashiers)}
  <div class="ui negative message">
    <div class="header">{$translate('No User found')}</div>
  </div>
{:else}
  <table class="playerlist ui celled unstackable striped selectable very compact unselectable table">
    <thead>
      <tr>
        <th style="width: 0px">#</th>
        <th>{$translate('First Name')}</th>
        <th>{$translate('Last Name')}</th>
        <th>{$translate('E-mail')}</th>
        <th>{$translate('Phone Number')}</th>
        <th>{$translate('Group')}</th>
        <th>{$translate('Referral')}</th>
        <!-- <th>{$translate('Daily Limit')}</th> -->
        <th>{$translate('Transaction Limit')}</th>
        {#if $me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin}
          <th style="width: 0px">Allow</th>
          <th style="width: 0px">{$translate('Options')}</th>
        {/if}
      </tr>
    </thead>
    <tbody>
      {#each searchCashiers ?? [] as user}
        <tr>
          <td class="numeric">{user.Id}</td>
          <td>{user.FirstName ?? ""}</td>
          <td>{user.LastName ?? ""}</td>
          <td>{user.Email ?? ""}</td>
          <td>{user.PhoneNumber ?? ""}</td>
          <td>{user.Group?.Name ?? ""}</td>
          <td>{user.Reference ?? ""}</td>
          <!-- <td>{formatMoney(user.Dailylimit?.InCents) || '$0.00'}</td> -->
          <td>{formatMoney(user.Txlimit?.InCents) || '$0.00'}</td>
          {#if $me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin}
            <td>
              <button class={`ui icon button ${user.Enabled ? 'positive' : 'negative'}`} on:click={() => DisablePlayer(user)}>
                {#if user.Enabled}
                  <i class="check icon"></i>
                {:else}
                  <i class="close icon"></i>
                {/if}
              </button>
            </td>
            <td style="white-space: nowrap;">
              <a class="ui primary icon small button" href="#/CashierList/edit/{user.Id}">
                <i class="edit icon"></i>
              </a>
            </td>
          {/if}
        </tr>
      {/each}
    </tbody>
  </table>
{/if}

<div class="ui container">
  {#if message.text}
    <div class={`ui ${message.type} message`}>
      <div class="header">
        {message.text}
      </div>
    </div>
  {/if}
</div>

<style>
  .center {
    text-align: center;
  }
  .hidden {
    display: none;
  }
  .unselectable {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
  .numeric {
    font-family: monospace;
    text-align: right !important;
  }
</style>